<template>
  <div>
    <v-form
      ref="form"
      :lazy-validation="true"
    >
      <v-row>
        <v-col cols="6">
          <v-select
            data-cy="select-application-profession"
            v-model="selectedApplication.professionId"
            :items="professions"
            outlined
            item-text="text"
            item-value="id"
            label="Yrke"
            clearable
            @click:clear="$nextTick(() => (selectedApplication.professionId = null))"
          ></v-select>
        </v-col>
        <v-col cols="6">
          <v-select
            v-if="hasProfessionOrientations(selectedApplication.professionId)"
            data-cy="select-application-orientations"
            v-model="professionOrientationsArrClone"
            outlined
            :items="orientations"
            label="Inriktning(ar) "
            multiple
          ></v-select>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="3">
          <v-select
            label="Handledare"
            outlined
            data-cy="select-instructor"
            :items="applicationCompanyEmployments"
            v-model="application.instructorId"
            item-text="fullName"
            item-value="id"
            clearable
            @click:clear="$nextTick(() => (application.instructorId = null))"
          />
        </v-col>
        <v-col cols="3">
          <v-select
            label="Arbetsplats"
            :items="applicationCompany.workplaces"
            outlined
            data-cy="select-workplace"
            v-model="application.workplaceId"
            item-text="name"
            item-value="id"
            clearable
            @click:clear="$nextTick(() => (application.workplaceId = null))"
          />
        </v-col>
        <v-col cols="3">
          <v-select
            v-if="applicationCompany.hasDepartments"
            label="Avdelning"
            :items="applicationDepartments"
            outlined
            v-model="application.departmentId"
            item-text="name"
            item-value="id"
            clearable
            @click:clear="$nextTick(() => (application.departmentId = null))"
          />
        </v-col>
      </v-row>

      <MessageBox
        type="alert"
        class="mb-3 mt-n4"
        v-if="!instructorHaveCertificate"
      >
        <h6>VAR VÄNLIG KONTROLLERA ATT HANDLEDAREN MÖTER KRAVEN FÖR ATT HANDLEDA UNDER GRUNDUTBILDNING</h6>
        <p>
          Den utsedda handledaren möter kraven för att handleda denna lärling under kvalificeringstiden men det är
          inte säkert att hen möter kraven för att handleda under grundutbildningen. Var vänlig kontrollera att
          handledaren har de meriter och behörigheter som krävs för grundutbildning.
        </p>
      </MessageBox>

      <v-row>
        <v-col cols="3">
          <v-select
            data-cy="select-application-type"
            v-model="selectedApplication.employmentTypeId"
            :items="employmentTypes"
            item-text="text"
            outlined
            item-value="id"
            label="Anställningsform"
            clearable
            @click:clear="$nextTick(() => (selectedApplication.employmentTypeId = null))"
          ></v-select>
        </v-col>
        <v-col cols="3">
          <v-menu
            data-cy="select-application-date"
            ref="menu1"
            v-model="menu1"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            max-width="290px"
            min-width="290px"
          >
            <template v-slot:activator="{on}">
              <v-text-field
                v-if="selectedApplication"
                v-model="defaultDateLocal"
                outlined
                label="Lärlingstidens startdatum"
                v-on="on"
                clearable
                @click:clear="$nextTick(() => (defaultDateLocal = null))"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="datePickerLocal"
              no-title
              @input="menu1 = false"
              :min="minDateLimit"
            ></v-date-picker>
          </v-menu>
        </v-col>
      </v-row>

      <MessageBox class="mb-2 mt-n4">
        <h6>LÄRLINGSTIDENS STARTDATUM GÄLLER FÖR EVENTUELL GRUNDUTBILDNING</h6>
        <p>
          Detta datum gäller enbart grundutbildningen. Om lärlingens bedöms ha meriterna som krävs för
          kvalificeringstid kan detta datum ändras i efterhand för att bättre spegla det datum när personen faktiskt
          påbörjade sin tid som lärling.
        </p>
      </MessageBox>
    </v-form>
  </div>
</template>

<script>
import API from '@/services/api.js'
import without from 'lodash/without'
import concat from 'lodash/concat'
import MessageBox from '@/components/common/MessageBox.vue'
import {InstructorAbilities, ApplicationProfession} from '@/components/mixins'
import filter from 'lodash/filter'
import { mapGetters } from 'vuex'
import get from 'lodash/get'

export default {
  name: 'ApplicationProfessionEdit',

  props: {
    professions: {
      type: Array,
      required: true
    },

    application: {
      type: Object,
      required: true
    },

    employmentTypes: {
      type: Array,
      required: true
    },

    pickerDate: {
      type: String,
      default: null
    },

    defaultDate: {
      type: String,
      default: null
    },

    professionOrientationsArr: {
      type: Array,
      required: true
    }
  },

  mixins: [InstructorAbilities, ApplicationProfession],

  components: {
    MessageBox
  },

  data: () => ({
    menu1: false
  }),

  computed: {
    ...mapGetters('application', ['applicationDepartments']),

    datePickerLocal: {
      get() {
        this.pickerDate
      },
      set(value) {
        this.$emit('change-date', value)
      }
    },

    defaultDateLocal: {
      get() {
        return this.defaultDate
      },
      set(value) {
        this.$emit('default-date', value)
      }
    },

    professionOrientationsArrClone: {
      get() {
        return this.professionOrientationsArr
      },
      set(value) {
        this.$emit('update-profession-orientations', value)
      }
    },

    minDateLimit() {
      if ( get(this, 'application.allCoursesApproved', false) )
        return this.subtractMonths(18, new Date()).toISOString().substr(0, 10)
      return null
    }
  },

  methods: {
    subtractMonths(numOfMonths, date = new Date()) {
      date.setMonth(date.getMonth() - numOfMonths);
      return date;
    },

    /**
     * @vuese
     * Used to update application with new profession selected
     * @arg The argument is an integer representing application  id and param is updated fields
     */

    saveProfession() {
      if (this.selectedApplication && this.selectedApplication.id) {
        if (this.defaultDateLocal == '' || this.defaultDateLocal == null) {
          this.defaultDateLocal = null
          this.datePickerLocal = null
        }
        let professionOrientations = []
        if (
          this.professionOrientationsArr &&
          this.professionOrientationsArr.length > 0 &&
          this.hasProfessionOrientations(this.selectedApplication.professionId)
        ) {
          const selIds = this.professionOrientationsArr
          const oldOnes = filter(this.selectedApplication.professionOrientations, function (o) {
            return selIds.includes(o.professionMachineOrientationId)
          })
          const newIds = without(this.professionOrientationsArr, ...oldOnes.map(o => o.professionMachineOrientationId))
          const newOnes = filter(this.orientations, function (o) {
            return newIds.includes(o.value)
          }).map(o => {
            return {applicationId: this.selectedApplication.id, professionMachineOrientationId: o.value}
          })
          professionOrientations = concat(oldOnes, newOnes)
        }

        let updateFields = {
          professionId: this.selectedApplication.professionId,
          employmentTypeId: this.selectedApplication.employmentTypeId,
          employmentDate: this.defaultDate,
          instructorId: this.application.instructorId,
          workplaceId: this.application.workplaceId,
          saveProfessionOrientations: professionOrientations,
          departmentId: this.application?.departmentId
        }

        API.updateApplication(this.selectedApplication.id, {data: updateFields})
          .then(response => {
            this.$emit('saved')
            this.$events.fire('showInfo', 'Anmälan sparad.')
          })
          .catch(error => {
            this.$events.fire(
              'showError',
              get(
                error,
                'response.data.message',
                'Det gick tyvärr inte att spara dina ändringar! Detta är ett ovanligt fel och om det händer igen v.v. kontakta BYN:s kansli.'
              )
            )
            console.log('There was an error:', error)
          })
      } else {
        this.$events.fire('showError', 'Ingen anmälan angiven, testa att ladda om sidan.')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
</style>